import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconHelp, IconGear, IconChevronRight, IconInfo } from './Icons';
import { useAuth } from '../context/AuthContext';

// Top-right account menu adapted from MamaMind. Answer Assist does not have
// accounts in v1 so we drop the user-profile rows and keep the three utility
// icons: Help, About, Settings. Each icon also opens the full dropdown for
// touch users who prefer text labels.

export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const items = [
    {
      Icon: IconHelp,
      label: 'Help and support',
      sub: 'Frequently asked questions',
      action: () => {
        setOpen(false);
        navigate('/support');
      },
    },
    {
      Icon: IconInfo,
      label: 'Privacy and terms',
      sub: 'What we collect, what we do not',
      action: () => {
        setOpen(false);
        navigate('/privacy');
      },
    },
    {
      Icon: IconGear,
      label: 'Settings',
      sub: 'Cloud AI, data, danger zone',
      action: () => {
        setOpen(false);
        navigate('/app/settings');
      },
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex items-center gap-1">
        {items.map((it) => {
          const Icon = it.Icon;
          return (
            <button
              key={it.label}
              onClick={() => {
                setOpen(false);
                it.action();
              }}
              aria-label={it.label}
              title={it.label}
              className="w-9 h-9 rounded-full text-ink/60 hover:text-ink hover:bg-ink/5 grid place-items-center transition"
            >
              <Icon size={20} />
            </button>
          );
        })}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          className="w-9 h-9 rounded-full text-ink/60 hover:text-ink hover:bg-ink/5 grid place-items-center transition"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="5" r="1.4" fill="currentColor" />
            <circle cx="12" cy="12" r="1.4" fill="currentColor" />
            <circle cx="12" cy="19" r="1.4" fill="currentColor" />
          </svg>
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-12 w-72 rounded-2xl bg-white/95 backdrop-blur-md border border-ink/5 shadow-xl shadow-ink/10 overflow-hidden z-40">
          <div className="px-4 py-3 border-b border-ink/5">
            <div className="text-sm font-medium text-ink">My Dyslexia Tools</div>
            <div className="text-xs text-ink/50">My Dyslexia Tools v0.1</div>
          </div>
          {items.map((it) => {
            const Icon = it.Icon;
            return (
              <button
                key={it.label}
                onClick={it.action}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/5 transition text-left"
              >
                <span className="text-accent">
                  <Icon size={18} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-ink">{it.label}</span>
                  <span className="block text-xs text-ink/50 truncate">{it.sub}</span>
                </span>
                <span className="text-ink/30">
                  <IconChevronRight size={14} />
                </span>
              </button>
            );
          })}
          {user && (
            <button
              onClick={async () => {
                setOpen(false);
                await signOut();
                navigate('/');
              }}
              className="w-full flex items-center gap-3 px-4 py-3 border-t border-ink/5 hover:bg-red-50 transition text-left text-red-600"
            >
              <span className="text-sm font-medium">Sign out</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
